/**
 * AR-1 Turn Detection Algorithm
 * Method ID: a1-turns-gyro-extrema
 *
 * Based on gyroscope Z-axis angular velocity extrema detection,
 * splits the skiing frame sequence into turns.
 *
 * 基于陀螺仪 Z 轴角速度极值检测，将滑雪帧序列切分为弯。
 */

import { AR1_PARAMS } from '../constants/algorithm-params';
import { TurnDirection, TurnDuration } from '../types/enums';
import type { SkiTickData } from '../types/ski-data';

// ===== Output Interfaces =====

/** 检测到的单个弯 / A single detected turn */
export interface DetectedTurn {
  turn_index: number;        // 1-based / 从 1 起
  start_ms: number;
  end_ms: number;
  duration_ms: number;
  direction: TurnDirection;
  duration_class: TurnDuration;
  peak_gyro_z: number;       // signed peak value / 有符号峰值
  start_frame_index: number;
  end_frame_index: number;
}

/** 转弯检测结果 / Turn detection result */
export interface TurnDetectionResult {
  turns: DetectedTurn[];
  total_score: number;       // 0-100
  confidence: number;        // 0.85
  left_count: number;
  right_count: number;
  short_count: number;
  medium_count: number;
  long_count: number;
  avg_peak_gyro: number;
}

// ===== Internal Types =====

/** 极值点 / An extremum point */
interface Extremum {
  type: 'max' | 'min';
  index: number;          // frame index in original array / 原始帧索引
  timestamp_ms: number;
  value: number;          // smoothed gyro Z value / 平滑后的陀螺仪 Z 值
}

// ===== Helper Functions =====

/**
 * Centered sliding average smoothing.
 * For boundary points where the window extends beyond the array,
 * the window is shrunk to the available range.
 *
 * 中心滑动平均平滑。窗口超出数组边界时，缩小窗口至可用范围。
 *
 * @param gyroZSeries - raw gyroscope Z series / 原始陀螺仪 Z 序列
 * @param window - sliding window size / 滑动窗口大小
 * @returns smoothed series / 平滑后序列
 */
export function smoothGyroZ(gyroZSeries: number[], window: number): number[] {
  const n = gyroZSeries.length;
  if (n === 0) return [];

  const half = Math.floor(window / 2);
  const result: number[] = new Array(n);

  for (let i = 0; i < n; i++) {
    // Clamp window to array boundaries / 将窗口限制在数组边界内
    const start = Math.max(0, i - half);
    const end = Math.min(n - 1, i + half);

    let sum = 0;
    let count = 0;
    for (let j = start; j <= end; j++) {
      sum += gyroZSeries[j];
      count++;
    }
    result[i] = sum / count;
  }

  return result;
}

/**
 * Find local extrema in a smoothed series.
 * Iterates from index 2 to length-3 (skipping first 2 and last 2 indices
 * where the smoothing window is incomplete).
 * Only records extrema where |value| > minAmplitude.
 *
 * 在平滑序列中查找局部极值。
 * 从索引 2 遍历到 length-3（跳过前 2 和后 2 个点，因平滑窗口不完整）。
 * 仅记录 |value| > minAmplitude 的极值。
 *
 * @param smoothedSeries - smoothed gyroscope Z series / 平滑后的陀螺仪 Z 序列
 * @param timestamps - corresponding timestamps in ms / 对应时间戳（毫秒）
 * @param minAmplitude - minimum |value| threshold / 最小绝对值阈值
 * @returns array of extrema sorted by index / 按索引排序的极值数组
 */
export function findExtrema(
  smoothedSeries: number[],
  timestamps: number[],
  minAmplitude: number
): Extremum[] {
  const n = smoothedSeries.length;
  const extrema: Extremum[] = [];

  // Skip first 2 and last 2 indices (incomplete smoothing window)
  // 跳过前 2 和后 2 个索引（平滑窗口不完整）
  for (let i = 2; i < n - 2; i++) {
    const prev = smoothedSeries[i - 1];
    const curr = smoothedSeries[i];
    const next = smoothedSeries[i + 1];

    // Local maximum: greater than both neighbors
    // 局部极大值：大于两侧相邻点
    if (curr > prev && curr > next && Math.abs(curr) > minAmplitude) {
      extrema.push({
        type: 'max',
        index: i,
        timestamp_ms: timestamps[i],
        value: curr,
      });
    }
    // Local minimum: less than both neighbors
    // 局部极小值：小于两侧相邻点
    else if (curr < prev && curr < next && Math.abs(curr) > minAmplitude) {
      extrema.push({
        type: 'min',
        index: i,
        timestamp_ms: timestamps[i],
        value: curr,
      });
    }
  }

  return extrema;
}

/**
 * Classify turn duration into Short / Medium / Long.
 * - < 500ms → Short
 * - 500–1500ms → Medium
 * - >= 1500ms → Long
 *
 * 将弯时长分类为 Short / Medium / Long。
 *
 * @param durationMs - duration in milliseconds / 时长（毫秒）
 * @returns TurnDuration enum value / 时长分类枚举值
 */
export function classifyDuration(durationMs: number): TurnDuration {
  if (durationMs < AR1_PARAMS.shortTurnThreshold) {
    return TurnDuration.Short;
  }
  if (durationMs < AR1_PARAMS.mediumTurnThreshold) {
    return TurnDuration.Medium;
  }
  return TurnDuration.Long;
}

/**
 * Merge turns separated by a small gap.
 * If the gap between prev.end_ms and next.start_ms is positive and < minInterval,
 * the two turns are merged: keep prev.start_ms, set end_ms = next.end_ms,
 * and peak_value = the one with larger |abs|.
 *
 * 合并间隔较小的弯。若前一个弯的 end_ms 到下一个弯的 start_ms 的间隔
 * 为正且 < minInterval，则合并：保留 prev.start_ms，end_ms = next.end_ms，
 * peak_value 为绝对值较大者。
 *
 * @param turns - array of detected turns / 检测到的弯数组
 * @param minInterval - minimum interval threshold in ms / 最小间隔阈值（毫秒）
 * @returns merged turns with re-indexed turn_index / 合并后并重新编号的弯数组
 */
export function mergeCloseTurns(
  turns: DetectedTurn[],
  minInterval: number
): DetectedTurn[] {
  if (turns.length <= 1) return turns;

  const merged: DetectedTurn[] = [];
  let current: DetectedTurn = { ...turns[0] };

  for (let i = 1; i < turns.length; i++) {
    const next = turns[i];
    const gap = next.start_ms - current.end_ms;

    // Merge only when there is a positive gap smaller than minInterval.
    // A gap of 0 (shared boundary) is not merged — it represents two
    // contiguous half-cycles, not a spurious split.
    // 仅当间隔为正且小于 minInterval 时合并。
    // 间隔为 0（共享边界）不合并——代表两个连续半周期，非虚假切分。
    if (gap > 0 && gap < minInterval) {
      // Extend end, pick peak with larger |abs|, reclassify
      // 延长终点，选择绝对值较大的峰值，重新分类
      current.end_ms = next.end_ms;
      current.end_frame_index = next.end_frame_index;
      current.duration_ms = current.end_ms - current.start_ms;

      if (Math.abs(next.peak_gyro_z) > Math.abs(current.peak_gyro_z)) {
        current.peak_gyro_z = next.peak_gyro_z;
        current.direction =
          next.peak_gyro_z > 0 ? TurnDirection.Left : TurnDirection.Right;
      }
      current.duration_class = classifyDuration(current.duration_ms);
    } else {
      merged.push(current);
      current = { ...next };
    }
  }
  merged.push(current);

  // Re-index turns after merging / 合并后重新编号
  return merged.map((turn, idx) => ({
    ...turn,
    turn_index: idx + 1,
  }));
}

/**
 * Compute Shannon entropy in bits (log2).
 * Accepts a distribution as an array of counts or probabilities.
 *
 * 计算香农熵（比特，log2）。接受计数或概率数组。
 *
 * @param distribution - array of counts or probabilities / 计数或概率数组
 * @returns entropy in bits / 熵（比特）
 */
export function shannonEntropy(distribution: number[]): number {
  const total = distribution.reduce((sum, v) => sum + v, 0);
  if (total === 0) return 0;

  let entropy = 0;
  for (const count of distribution) {
    if (count > 0) {
      const p = count / total;
      entropy -= p * Math.log2(p);
    }
  }

  return entropy;
}

// ===== Internal: Extrema → Turns Pairing =====

/**
 * Pair adjacent opposite-type extrema into turns (non-overlapping).
 * Each extremum is used in at most one pair. Only (max, min) or (min, max)
 * pairs form turns; same-type adjacent extrema are skipped.
 *
 * 将相邻的相反类型极值配对为弯（不重叠）。每个极值最多使用一次。
 * 仅 (max, min) 或 (min, max) 配对形成弯；同类型相邻极值跳过。
 *
 * @param extrema - sorted extrema array / 排序后的极值数组
 * @returns detected turns / 检测到的弯
 */
function pairExtremaToTurns(extrema: Extremum[]): DetectedTurn[] {
  const turns: DetectedTurn[] = [];
  let turnIndex = 1;

  let i = 0;
  while (i < extrema.length - 1) {
    const prev = extrema[i];
    const next = extrema[i + 1];

    if (prev.type !== next.type) {
      // Opposite-type pair → form a turn / 相反类型配对 → 形成弯
      const startMs = prev.timestamp_ms;
      const endMs = next.timestamp_ms;
      const durationMs = endMs - startMs;

      // Peak = extremum with larger |abs| / 峰值 = 绝对值较大的极值
      const peakValue =
        Math.abs(prev.value) >= Math.abs(next.value)
          ? prev.value
          : next.value;

      // Direction: peak > 0 → Left, peak <= 0 → Right
      // 方向：峰值 > 0 → 左弯，峰值 <= 0 → 右弯
      const direction =
        peakValue > 0 ? TurnDirection.Left : TurnDirection.Right;

      turns.push({
        turn_index: turnIndex++,
        start_ms: startMs,
        end_ms: endMs,
        duration_ms: durationMs,
        direction,
        duration_class: classifyDuration(durationMs),
        peak_gyro_z: peakValue,
        start_frame_index: prev.index,
        end_frame_index: next.index,
      });

      i += 2; // Both extrema consumed / 两个极值均已使用
    } else {
      // Same type → skip current, try pairing next / 同类型 → 跳过当前
      i += 1;
    }
  }

  return turns;
}

// ===== Internal: Scoring =====

/**
 * Compute total turn detection score (0–100).
 *
 * 评分公式 / Scoring formula:
 *   score = min(turns_count * 2, 30)                              // turn count, max 30
 *         + min(25, max(0, 100 - |left/right - 1| * 30))         // balance, max 25
 *         + shannonEntropy([short, medium, long]) / log2(3) * 25 // diversity, max 25
 *         + min(avg_peak_gyro * 20, 20)                           // intensity, max 20
 *
 * @returns total score 0–100 / 总分 0–100
 */
function computeScore(
  turnsCount: number,
  leftCount: number,
  rightCount: number,
  shortCount: number,
  mediumCount: number,
  longCount: number,
  avgPeakGyro: number
): number {
  // 1. Turn count score (max 30) / 弯数得分（最高 30）
  const turnCountScore = Math.min(turnsCount * 2, 30);

  // 2. Left/right balance score (max 25) / 左右平衡得分（最高 25）
  let balanceScore: number;
  if (turnsCount === 0 || rightCount === 0) {
    // No turns or rightCount is 0 (division by zero) → unbalanced
    // 无弯或右弯数为 0（除零）→ 不平衡
    balanceScore = 0;
  } else {
    const ratio = leftCount / rightCount;
    balanceScore = Math.max(0, Math.min(25, 100 - Math.abs(ratio - 1) * 30));
  }

  // 3. Duration diversity score (max 25) / 时长多样性得分（最高 25）
  // Max entropy for 3 categories = log2(3) ≈ 1.585
  const entropy = shannonEntropy([shortCount, mediumCount, longCount]);
  const maxEntropy = Math.log2(3);
  const diversityScore = maxEntropy > 0 ? (entropy / maxEntropy) * 25 : 0;

  // 4. Intensity score (max 20) / 强度得分（最高 20）
  const intensityScore = Math.min(avgPeakGyro * 20, 20);

  const total =
    turnCountScore + balanceScore + diversityScore + intensityScore;

  // Clamp to [0, 100] / 限制在 [0, 100]
  return Math.max(0, Math.min(100, total));
}

// ===== Main Entry Point =====

/**
 * AR-1 Turn Detection: detect turns from skiing frame data.
 * Method ID: a1-turns-gyro-extrema
 *
 * Algorithm steps / 算法步骤:
 * 1. Smooth gyroscope Z with centered sliding average (window=5)
 *    用窗口=5 的中心滑动平均平滑陀螺仪 Z
 * 2. Detect local extrema (max/min) above amplitude threshold
 *    检测幅值超过阈值的局部极值（max/min）
 * 3. Pair adjacent opposite-type extrema into turns
 *    将相邻的相反类型极值配对为弯
 * 4. Merge turns with small gaps (< minTurnInterval)
 *    合并间隔较小的弯（< minTurnInterval）
 * 5. Classify direction (Left/Right) and duration (Short/Medium/Long)
 *    分类方向（左/右）和时长（短/中/长）
 * 6. Compute total score (0–100) and confidence (0.85)
 *    计算总分（0–100）和置信度（0.85）
 *
 * Deterministic per PO-5.4 (no random numbers).
 * 按 PO-5.4 确定性实现（无随机数）。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @returns TurnDetectionResult with turns and scoring / 转弯检测结果
 */
export function detectTurns(frames: SkiTickData[]): TurnDetectionResult {
  // Edge case: not enough frames for smoothing / 边界情况：帧数不足以平滑
  if (frames.length < 5) {
    return {
      turns: [],
      total_score: 0,
      confidence: 0,
      left_count: 0,
      right_count: 0,
      short_count: 0,
      medium_count: 0,
      long_count: 0,
      avg_peak_gyro: 0,
    };
  }

  // Extract gyroscope Z series and timestamps / 提取陀螺仪 Z 序列和时间戳
  const gyroZSeries = frames.map((f) => f.gyroscope.z);
  const timestamps = frames.map((f) => f.timestamp_ms);

  // Step 1: Smooth gyroscope Z / 步骤 1：平滑陀螺仪 Z
  const smoothedGyroZ = smoothGyroZ(gyroZSeries, AR1_PARAMS.smoothingWindow);

  // Step 2: Find extrema / 步骤 2：查找极值
  const extrema = findExtrema(
    smoothedGyroZ,
    timestamps,
    AR1_PARAMS.minGyroAmplitude
  );

  // Step 3: Pair extrema into turns / 步骤 3：配对极值为弯
  const rawTurns = pairExtremaToTurns(extrema);

  // Step 4: Merge close turns / 步骤 4：合并相近的弯
  const turns = mergeCloseTurns(rawTurns, AR1_PARAMS.minTurnInterval);

  // Step 5: Aggregate counts / 步骤 5：统计计数
  const leftCount = turns.filter(
    (t) => t.direction === TurnDirection.Left
  ).length;
  const rightCount = turns.filter(
    (t) => t.direction === TurnDirection.Right
  ).length;
  const shortCount = turns.filter(
    (t) => t.duration_class === TurnDuration.Short
  ).length;
  const mediumCount = turns.filter(
    (t) => t.duration_class === TurnDuration.Medium
  ).length;
  const longCount = turns.filter(
    (t) => t.duration_class === TurnDuration.Long
  ).length;

  // Average peak gyro (absolute value) / 平均峰值陀螺仪（绝对值）
  const avgPeakGyro =
    turns.length > 0
      ? turns.reduce((sum, t) => sum + Math.abs(t.peak_gyro_z), 0) /
        turns.length
      : 0;

  // Step 6: Compute total score / 步骤 6：计算总分
  const totalScore = computeScore(
    turns.length,
    leftCount,
    rightCount,
    shortCount,
    mediumCount,
    longCount,
    avgPeakGyro
  );

  return {
    turns,
    total_score: totalScore,
    confidence: AR1_PARAMS.confidence,
    left_count: leftCount,
    right_count: rightCount,
    short_count: shortCount,
    medium_count: mediumCount,
    long_count: longCount,
    avg_peak_gyro: avgPeakGyro,
  };
}
